// AddressShareCard — V2 premium (2026-06) : carte visuelle AYA générée en canvas
// (code + mini-carte Mapbox statique) partageable sur WhatsApp (navigator.share
// avec fichier, fallback téléchargement + lien wa.me).
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import "./DomicileGeolocModal.css";

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN || "";

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

function loadImage(src, crossOrigin) {
  return new Promise((resolve) => {
    const img = new Image();
    if (crossOrigin) img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

async function drawCard(addr) {
  const W = 1080, H = 1350;
  const canvas = document.createElement("canvas");
  canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext("2d");

  // Fond vert profond + halo
  const bg = ctx.createLinearGradient(0, 0, 0, H);
  bg.addColorStop(0, "#0A3A22"); bg.addColorStop(0.45, "#062818"); bg.addColorStop(1, "#051F13");
  ctx.fillStyle = bg; ctx.fillRect(0, 0, W, H);
  const halo = ctx.createRadialGradient(W * 0.85, H * 0.12, 60, W * 0.85, H * 0.12, 700);
  halo.addColorStop(0, "rgba(14,90,50,0.55)"); halo.addColorStop(1, "rgba(14,90,50,0)");
  ctx.fillStyle = halo; ctx.fillRect(0, 0, W, H);

  // Cadre or
  ctx.strokeStyle = "rgba(201,162,75,0.55)"; ctx.lineWidth = 3;
  roundRect(ctx, 36, 36, W - 72, H - 72, 44); ctx.stroke();

  // Masque AYA (logo) dans un anneau or
  const logo = await loadImage("/aya/notch.png");
  const cx = W / 2, logoY = 150, lr = 74;
  ctx.save();
  ctx.beginPath(); ctx.arc(cx, logoY, lr, 0, Math.PI * 2); ctx.closePath();
  ctx.fillStyle = "#04150C"; ctx.fill(); ctx.clip();
  if (logo) ctx.drawImage(logo, cx - lr, logoY - lr, lr * 2, lr * 2);
  ctx.restore();
  ctx.strokeStyle = "#D9B45B"; ctx.lineWidth = 5;
  ctx.beginPath(); ctx.arc(cx, logoY, lr, 0, Math.PI * 2); ctx.stroke();
  ctx.strokeStyle = "rgba(217,180,91,0.25)"; ctx.lineWidth = 12;
  ctx.beginPath(); ctx.arc(cx, logoY, lr + 10, 0, Math.PI * 2); ctx.stroke();

  // Eyebrow + nom
  ctx.textAlign = "center";
  ctx.fillStyle = "#D9B45B";
  ctx.font = "800 26px Manrope, Arial";
  ctx.fillText("A D R E S S E   O F F I C I E L L E   A Y A", cx, 290);
  ctx.fillStyle = "#FFFFFF";
  ctx.font = "900 66px Manrope, Arial";
  const name = addr.name || "Mon Domicile";
  ctx.fillText(name.length > 24 ? `${name.slice(0, 23)}…` : name, cx, 368);
  ctx.fillStyle = "rgba(236,245,238,0.75)";
  ctx.font = "600 32px Manrope, Arial";
  const sub = [addr.is_home ? "Adresse principale" : "Adresse", addr.commune || addr.commune_name].filter(Boolean).join(" · ");
  ctx.fillText(sub, cx, 420);

  // Mini-carte Mapbox statique
  const mapX = 80, mapY = 470, mapW = W - 160, mapH = 520, mapR = 36;
  let mapImg = null;
  if (MAPBOX_TOKEN && Number.isFinite(addr.lat) && Number.isFinite(addr.lng)) {
    const url = `https://api.mapbox.com/styles/v1/mapbox/streets-v12/static/pin-l+E2A832(${addr.lng},${addr.lat})/${addr.lng},${addr.lat},15.2,0/${Math.round(mapW / 2)}x${Math.round(mapH / 2)}@2x?access_token=${MAPBOX_TOKEN}&logo=false&attribution=false`;
    mapImg = await loadImage(url, true);
  }
  ctx.save();
  roundRect(ctx, mapX, mapY, mapW, mapH, mapR); ctx.clip();
  if (mapImg) {
    ctx.drawImage(mapImg, mapX, mapY, mapW, mapH);
  } else {
    const ph = ctx.createLinearGradient(mapX, mapY, mapX, mapY + mapH);
    ph.addColorStop(0, "#0E5A32"); ph.addColorStop(1, "#083B20");
    ctx.fillStyle = ph; ctx.fillRect(mapX, mapY, mapW, mapH);
    ctx.fillStyle = "#F2C66B"; ctx.font = "900 110px Arial";
    ctx.fillText("📍", cx, mapY + mapH / 2 + 38);
  }
  ctx.restore();
  ctx.strokeStyle = "rgba(201,162,75,0.6)"; ctx.lineWidth = 4;
  roundRect(ctx, mapX, mapY, mapW, mapH, mapR); ctx.stroke();

  // Plaque code
  const plX = 110, plY = 1050, plW = W - 220, plH = 150;
  const plBg = ctx.createLinearGradient(plX, plY, plX, plY + plH);
  plBg.addColorStop(0, "rgba(20,90,50,0.85)"); plBg.addColorStop(1, "rgba(10,50,28,0.85)");
  ctx.fillStyle = plBg;
  roundRect(ctx, plX, plY, plW, plH, 28); ctx.fill();
  ctx.strokeStyle = "rgba(201,162,75,0.7)"; ctx.lineWidth = 3;
  roundRect(ctx, plX, plY, plW, plH, 28); ctx.stroke();
  ctx.fillStyle = "rgba(236,245,238,0.6)";
  ctx.font = "800 22px Manrope, Arial";
  ctx.fillText("C O D E   A Y A", cx, plY + 48);
  ctx.fillStyle = "#F2C66B";
  ctx.font = "800 64px 'Courier New', monospace";
  ctx.fillText(addr.address_civ || "", cx, plY + 118);

  // Footer
  ctx.fillStyle = "rgba(236,245,238,0.65)";
  ctx.font = "600 28px Manrope, Arial";
  ctx.fillText("Trouve-moi exactement avec AYA  ·  ayaai.africa", cx, 1282);

  return canvas;
}

export default function AddressShareCard({ open, addr, onClose }) {
  const [dataUrl, setDataUrl] = useState(null);
  const [busy, setBusy] = useState(false);
  const canvasRef = useRef(null);

  useEffect(() => {
    if (!open || !addr) return;
    setDataUrl(null);
    let cancelled = false;
    drawCard(addr).then((canvas) => {
      if (cancelled) return;
      canvasRef.current = canvas;
      setDataUrl(canvas.toDataURL("image/png"));
    });
    return () => { cancelled = true; };
  }, [open, addr]);

  if (!open || !addr) return null;

  const shareText = `📍 Mon adresse AYA : ${addr.name || "Domicile"} — ${addr.address_civ}\nTrouve-moi exactement avec AYA · ayaai.africa`;
  const fileName = `adresse-aya-${(addr.address_civ || "code").toLowerCase()}.png`;

  const getBlob = () => new Promise((resolve) => {
    if (!canvasRef.current) return resolve(null);
    canvasRef.current.toBlob((b) => resolve(b), "image/png");
  });

  const download = async () => {
    const blob = await getBlob();
    if (!blob) return false;
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = fileName;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 4000);
    return true;
  };

  const shareWhatsApp = async () => {
    setBusy(true);
    try {
      const blob = await getBlob();
      if (blob && navigator.canShare && navigator.canShare({ files: [new File([blob], fileName, { type: "image/png" })] })) {
        await navigator.share({
          files: [new File([blob], fileName, { type: "image/png" })],
          text: shareText,
        });
        setBusy(false);
        return;
      }
    } catch (_) { /* partage annulé ou non supporté → fallback */ }
    // Fallback : télécharge l'image + ouvre WhatsApp avec le texte.
    await download();
    window.open(`https://wa.me/?text=${encodeURIComponent(shareText)}`, "_blank", "noopener");
    toast.info("Image téléchargée — joins-la à ton message WhatsApp 📎");
    setBusy(false);
  };

  const copyCode = () => {
    navigator.clipboard?.writeText(addr.address_civ || "");
    toast.success("Code copié ✓");
  };

  return (
    <div data-testid="address-share-overlay" className="dgm-overlay" onClick={onClose}>
      <div className="dgm-modal" style={{ maxWidth: 440 }} onClick={(e) => e.stopPropagation()} data-testid="address-share-modal">
        <button onClick={onClose} className="dgm-close" data-testid="address-share-close" aria-label="Fermer">✕</button>
        <div className="dgm-eyebrow">Partager mon adresse</div>
        <h3 className="dgm-title">✨ Ta carte <span className="dgm-aya">AYA</span></h3>
        <div style={{ borderRadius: 18, overflow: "hidden", border: "1px solid rgba(201,162,75,0.45)", boxShadow: "0 14px 40px rgba(0,0,0,0.45)", marginBottom: 16, minHeight: 220, display: "grid", placeItems: "center", background: "rgba(4,18,11,0.4)" }}>
          {dataUrl ? (
            <img src={dataUrl} alt={`Carte adresse AYA ${addr.address_civ}`} data-testid="address-share-preview" style={{ display: "block", width: "100%" }} />
          ) : (
            <div className="dgm-locating" style={{ padding: "40px 10px" }}>
              <div className="dgm-radar">
                <span className="dgm-radar-ring" /><span className="dgm-radar-ring" /><span className="dgm-radar-ring" />
                <span className="dgm-radar-pin">🗺️</span>
              </div>
              <div className="dgm-locating-txt">Génération de ta carte…</div>
            </div>
          )}
        </div>
        <div className="dgm-actions" style={{ flexDirection: "column" }}>
          <button onClick={shareWhatsApp} disabled={busy || !dataUrl} className="dgm-btn dgm-btn-primary" data-testid="address-share-whatsapp" style={{ width: "100%" }}>
            {busy ? "…" : "🟢 Partager sur WhatsApp"}
          </button>
          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={download} disabled={!dataUrl} className="dgm-btn dgm-btn-secondary" data-testid="address-share-download" style={{ flex: 1 }}>⬇️ Télécharger</button>
            <button onClick={copyCode} className="dgm-btn dgm-btn-secondary" data-testid="address-share-copy" style={{ flex: 1 }}>📋 Copier le code</button>
          </div>
        </div>
      </div>
    </div>
  );
}
